import { zql } from '../../schema.js';
import { changedItems, regularDoseUnit } from '../../util/event-log-items.js';
const STATUS_SPEC = { field: 'status', valueKey: '{value}.title', valueNs: 'activity' };
/** Date, time, status, duration, volume and note changes of a prescription. Status texts are activity status titles. */
export function sequenceLogItems(prev, next) {
    const items = changedItems(prev, next, [
        { field: 'date', key: 'therapyDate', type: 'date' },
        { field: 'time' },
        STATUS_SPEC,
        { field: 'duration', unit: 'min' },
        { field: 'volume', unit: 'ml' },
        { field: 'therapy_note', key: 'therapyNote' },
    ]);
    return [...items, ...pharmacyNoteItems(prev, next)];
}
/** Carrier solution and application form changes of a prescription, by name. */
export async function sequenceNameItems(tx, prev, next) {
    const idChange = (key) => key in next && (prev?.[key] ?? null) !== (next[key] ?? null)
        ? { old: prev?.[key] ?? null, new: next[key] ?? null }
        : undefined;
    const carrierIds = idChange('carrierSolutionId');
    const formIds = idChange('applicationFormId');
    const drugName = async (id) => id ? ((await tx.run(zql.drugCatalogItems.where('id', id).one()))?.name ?? null) : null;
    const formName = async (id) => id ? ((await tx.run(zql.applicationForms.where('id', id).one()))?.name ?? null) : null;
    return [
        ...nameChangeItems('carrier', carrierIds && { old: await drugName(carrierIds.old), new: await drugName(carrierIds.new) }),
        ...nameChangeItems('application_form', formIds && { old: await formName(formIds.old), new: await formName(formIds.new) }),
    ];
}
/** Pharmacy notes are hidden from nurses, and nurses see the patient history: never log the text. */
export function pharmacyNoteItems(prev, next) {
    if (!('pharmacyNote' in next))
        return [];
    const before = prev?.pharmacyNote || null;
    const after = next.pharmacyNote || null;
    if (before === after)
        return [];
    const value = !before ? 'added' : !after ? 'removed' : 'changed';
    return [{ field: 'pharmacy_note', value, valueKey: 'event.value.note.{value}' }];
}
/** Name variant when only a note changed. */
export function noteVariant(items) {
    const fields = new Set(items.map(item => item.field));
    if (fields.size !== 1)
        return undefined;
    if (fields.has('therapy_note'))
        return 'note';
    if (fields.has('pharmacy_note'))
        return 'pharmacy_note';
    return undefined;
}
export function statusChangeItems(oldStatus, newStatus) {
    return changedItems({ status: oldStatus }, { status: newStatus }, [STATUS_SPEC]);
}
/**
 * Medicine name, dose and total. prev only = removed, next only = added, both = changed.
 * `previousName` is the name before the change when the substance or trade name was switched.
 */
export function medicineItems(prev, next, { name, doseUnit, previousName }) {
    if (!next)
        return prev ? [{ field: 'medicine', value: name }] : [];
    const unit = regularDoseUnit(next.calculationMethod, doseUnit);
    if (!prev) {
        return [
            { field: 'medicine', value: name },
            { field: 'dose', value: next.regularDose ?? null, unit },
            { field: 'total', value: next.totalAmount ?? null, unit: doseUnit },
        ];
    }
    const items = changedItems(prev, next, [
        { field: 'dose', key: 'regularDose', label: name, unit },
        { field: 'dose_proportion', key: 'doseProportion', unit: '%' },
        { field: 'calculation_method', key: 'calculationMethod' },
        { field: 'total', key: 'totalAmount', unit: doseUnit },
    ]);
    if (previousName !== undefined && previousName !== name) {
        return [{ field: 'medicine', old: previousName, new: name }, ...items];
    }
    // Name the medicine when the dose line (which carries the name) is not there, e.g. a recalculation.
    return items.some(item => item.field === 'dose') ? items : [{ field: 'medicine', value: name }, ...items];
}
function nameChangeItems(field, change) {
    return change && change.old !== change.new ? [{ field, old: change.old, new: change.new }] : [];
}
/** Changes of a protocol therapy day; carrier and application form are compared by name. */
export function protocolDayItems(prev, next, names) {
    return [
        ...changedItems(prev, next, [
            { field: 'day' },
            { field: 'time' },
            { field: 'duration', unit: 'min' },
            { field: 'volume', unit: 'ml' },
            { field: 'therapy_note', key: 'therapyNote' },
        ]),
        ...pharmacyNoteItems(prev, next),
        ...nameChangeItems('carrier', names.carrier),
        ...nameChangeItems('application_form', names.applicationForm),
    ];
}
/** The pharmacist's choices at validation: trade name per medicine and changed administration values. */
export function validationItems(params) {
    return [
        ...params.medicines.flatMap(({ substance, tradeName }) => tradeName ? [{ field: 'trade_name', label: substance, value: tradeName }] : []),
        ...changedItems(params.prev, params.next, [
            { field: 'duration', unit: 'min' },
            { field: 'volume', unit: 'ml' },
        ]),
        ...nameChangeItems('carrier', params.carrier),
        ...nameChangeItems('application_form', params.applicationForm),
    ];
}
/** medicineItems with the trade name (or drug name) and dose unit looked up. */
export async function medicineLogItems(tx, prev, next) {
    const row = next ?? prev;
    if (!row)
        return [];
    const { name, doseUnit } = await lookupMedicineName(tx, row);
    // only fields that are part of the update count as switched
    const switchedField = (key) => prev && next && key in next && (prev[key] ?? null) !== (next[key] ?? null);
    const switched = switchedField('activeSubstanceId') || switchedField('prescribedNameId');
    const previousName = switched && prev ? (await lookupMedicineName(tx, prev)).name : undefined;
    return medicineItems(prev, next, { name, doseUnit, previousName });
}
/** Trade name (or drug name) and dose unit of a medicine row. */
async function lookupMedicineName(tx, row) {
    const drug = row.activeSubstanceId
        ? await tx.run(zql.drugCatalogItems
            .where('id', row.activeSubstanceId)
            .related('activeSubstanceDetails', details => details.related('doseUnit'))
            .one())
        : undefined;
    const trade = row.prescribedNameId
        ? await tx.run(zql.prescribedNames.where('id', row.prescribedNameId).one())
        : undefined;
    return { name: trade?.name ?? drug?.name ?? '-', doseUnit: drug?.activeSubstanceDetails?.doseUnit?.displayName ?? 'mg' };
}
//# sourceMappingURL=medicine-log-items.js.map